import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Container, Tabs, Tab,
} from 'react-bootstrap';
import styled from '@emotion/styled';

import useBurnCenter from 'hooks/useBurnCenter';
import useUser from 'hooks/useUser';
import { FlexCenter } from 'resources/styles/display';
import Loader from 'components/elements/Loader';
import UsersList from 'pages/users/admin/UsersList';
import AboutTab from '../components/AboutTab';
import BenchmarkTab from '../components/BenchmarkTab';
import AdminTab from '../components/AdminTab';

export const BurnCenterTabLabels = {
  About: 'About',
  BenchmarkCriteria: 'Bed Capacity',
  Users: 'Users',
  Admin: 'Setup',
} as const;

type TabKey = keyof typeof BurnCenterTabLabels;

const Title = styled.h2`
  margin-bottom: 0.5rem;
`;

const DetailPage: React.FC = () => {
  const params = useParams();
  const id = params.id as string;

  const { burnCenter, loading, refetch } = useBurnCenter(id ?? '');

  const { isAdmin, isBdataAmin, isAbaAdmin } = useUser();

  const canEditAbout = isAdmin || isBdataAmin;
  const canEditBenchmark = isBdataAmin || isAdmin;
  const canSeeUsers = isAdmin || isBdataAmin;
  const canSeeAdmin = isAdmin;

  const [searchParams, setSearchParams] = useSearchParams();
  const queryTab = (searchParams.get('tab') || 'About') as TabKey;
  const [activeTab, setActiveTab] = React.useState<TabKey>(queryTab);

  React.useEffect(() => {
    const urlTab = (searchParams.get('tab') || 'About') as TabKey;
    if (urlTab !== activeTab) setActiveTab(urlTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const onTabChange = (key: string | null) => {
    if (!key) return;
    setActiveTab(key as TabKey);
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    setSearchParams(next);
  };

  const visibleTabs: Array<{ key: TabKey; title: string }> = [
    { key: 'About', title: BurnCenterTabLabels.About },
    { key: 'BenchmarkCriteria', title: BurnCenterTabLabels.BenchmarkCriteria },
    ...(canSeeUsers
      ? [{ key: 'Users' as TabKey, title: BurnCenterTabLabels.Users }]
      : []),
    ...(canSeeAdmin
      ? [{ key: 'Admin' as TabKey, title: BurnCenterTabLabels.Admin }]
      : []),
  ];

  return (
    <Container className="site-content">
      {!id && <div className="text-danger">Invalid Burn Center ID</div>}

      {loading && (
        <FlexCenter
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.4)',
          }}
        >
          <Loader />
        </FlexCenter>
      )}

      {!loading && id && burnCenter && (
        <>
          <Title>
            {burnCenter.name}
            {' '}
            (
            {burnCenter.hospitalName}
            )
          </Title>

          <Tabs
            id="burncenter-details-tabs"
            activeKey={activeTab}
            onSelect={onTabChange}
            mountOnEnter
          >
            {visibleTabs.map(({ key, title }) => (
              <Tab key={key} eventKey={key} title={title}>
                {key === 'About' && (
                  <div className="border border-top-0 p-3">
                    <AboutTab
                      id={id}
                      canEdit={canEditAbout}
                      canEditCenterIdentity={isBdataAmin}
                      benchmark={burnCenter.benchmark}
                      values={{
                        name: burnCenter.name,
                        hospitalName: burnCenter.hospitalName,
                        addressLine1: burnCenter.addressLine1,
                        addressLine2: burnCenter.addressLine2,
                        addressLine3: burnCenter.addressLine3,
                        city: burnCenter.city,
                        region: burnCenter.region,
                        zipPostalCode: burnCenter.zipPostalCode,
                        location: burnCenter.location ?? null,
                        country: burnCenter.country ?? null,
                        phoneNumber: burnCenter.phoneNumber,
                        tollFreeNumber: burnCenter.tollFreeNumber,
                        faxNumber: burnCenter.faxNumber,
                        email: burnCenter.email,
                        website: burnCenter.website,
                      }}
                      onSaved={() => {
                        refetch();
                      }}
                    />
                  </div>
                )}

                {key === 'BenchmarkCriteria' && (
                  <div className="border border-top-0 p-3">
                    <BenchmarkTab
                      id={id}
                      canEdit={canEditBenchmark}
                      values={burnCenter.benchmark ?? {}}
                      onSaved={refetch}
                    />
                  </div>
                )}

                {key === 'Users' && (
                  <div className="border border-top-0 p-3">
                    <UsersList source="burn-centers" />
                  </div>
                )}

                {key === 'Admin' && (
                  <div className="border border-top-0 p-3">
                    <AdminTab
                      id={id}
                      canEdit={!isAbaAdmin && canSeeAdmin}
                      values={{
                        burnCenterAccessLevel: burnCenter.burnCenterAccessLevel,
                        sigmaTeam: burnCenter.sigmaTeam,
                        jwtSigmaWorkspaceLandingLink: burnCenter.jwtSigmaWorkspaceLandingLink,
                      }}
                      onSaved={refetch}
                    />
                  </div>
                )}
              </Tab>
            ))}
          </Tabs>
        </>
      )}
    </Container>
  );
};

export default DetailPage;
